import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ResponsiveModal, ResponsiveButton } from '@/components/ui/responsive-modal';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Camera, Download, Filter, Plus, RotateCw, CheckCircle2, AlertCircle, Trash2, Upload, Loader2, Edit2, LogOut, Settings, Grid3x3, List, Lock, Unlock, Save, FolderTree, GripVertical, FileText } from 'lucide-react';
import PortraitCropModal from './PortraitCropModal';
import { DndContext, closestCenter, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function ProjectDetail({ user, onLogout }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [categories, setCategories] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [filterMissing, setFilterMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('projectDetailViewMode') || 'grid';
  });
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedImageDataUrl, setSelectedImageDataUrl] = useState(null); // Pre-loaded image data
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [uploadData, setUploadData] = useState({
    file: null,
    caption: '',
    notes: '',
    preview: null,
    isEdit: false,
    existingPhotoId: null,
    existingPhotoUrl: null,
  });
  const [exporting, setExporting] = useState(false);
  const [showAddSelectionModal, setShowAddSelectionModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);
  const [deleteCategoryName, setDeleteCategoryName] = useState('');
  const [deleteCategoryConfirmText, setDeleteCategoryConfirmText] = useState('');
  const [showImportCsvModal, setShowImportCsvModal] = useState(false);
  const [csvTemplates, setCsvTemplates] = useState([]);
  const [selectedCsvTemplateId, setSelectedCsvTemplateId] = useState('');
  const [importingCsv, setImportingCsv] = useState(false);
  
  // Photo loading state for iOS/Android file validation
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [photoLoadError, setPhotoLoadError] = useState(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [showAddCustomModal, setShowAddCustomModal] = useState(false);
  const [customPhotoName, setCustomPhotoName] = useState('');
  const [customPhotoCategory, setCustomPhotoCategory] = useState('');
  const [customPhotoRequired, setCustomPhotoRequired] = useState(false);
  const [deletePhotoId, setDeletePhotoId] = useState(null);
  const [showExportPreview, setShowExportPreview] = useState(false);
  const [exportSettings, setExportSettings] = useState({
    burn_in_project_name: true,
    burn_in_timestamp: true,
    caption_outside_with_bg: true,
    prepend_category_to_caption: false,
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    customer: '',
  });

  // Helper function to format error messages properly
  const formatErrorMessage = (error, fallbackMessage = 'An error occurred') => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    } else if (Array.isArray(detail)) {
      return detail[0]?.msg || fallbackMessage;
    } else if (typeof detail === 'object' && detail !== null) {
      return detail.msg || fallbackMessage;
    }
    return error.message || fallbackMessage;
  };

  useEffect(() => {
    loadProjectData();
    loadCsvTemplates();
  }, [projectId, filterMissing]);

  const loadCsvTemplates = async () => {
    try {
      const response = await axiosInstance.get('/csv-templates');
      setCsvTemplates(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load CSV templates:', error);
    }
  };

  const loadProjectData = async (overrideFilterMissing = null) => {
    try {
      const filterValue = overrideFilterMissing !== null ? overrideFilterMissing : filterMissing;
      const [projectRes, categoriesRes, photosRes] = await Promise.all([
        axiosInstance.get(`/projects/${projectId}`),
        axiosInstance.get(`/projects/${projectId}/categories`),
        axiosInstance.get(`/projects/${projectId}/photos`, {
          params: { filter_missing: filterValue },
        }),
      ]);
      setProject(projectRes.data);
      setCategories(categoriesRes.data);
      setPhotos(photosRes.data);
    } catch (error) {
      console.error('Load project error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Handle specific error cases
      if (error.response?.status === 404) {
        toast.error('Project not found. It may have been deleted.');
      } else if (error.message === 'Network Error') {
        toast.error('Unable to load project. Please check your connection and try again.');
      } else {
        const errorMsg = formatErrorMessage(error, 'Failed to load project data');
        toast.error(errorMsg);
      }
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large (max 10MB)');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadData({ ...uploadData, file, preview: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditProject = () => {
    setEditForm({
      name: project.name,
      customer: project.customer || '',
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.put(`/projects/${projectId}`, {
        name: editForm.name,
        customer: editForm.customer,
        version: project.version,
      });
      toast.success('Project updated successfully');
      setShowEditModal(false);
      await loadProjectData();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('Version conflict. Please refresh and try again.');
      } else {
        toast.error('Failed to update project');
      }
    }
  };

  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (uploading) {
      return;
    }

    setUploading(true);

    try {
      if (uploadData.file) {
        // Uploading a photo (new or replacing existing)
        const formData = new FormData();
        formData.append('caption', uploadData.caption);
        formData.append('notes', uploadData.notes || '');
        formData.append('file', uploadData.file);

        if (uploadData.existingPhotoId) {
          // Replace existing photo
          await axiosInstance.put(`/photos/${uploadData.existingPhotoId}/edit`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          toast.success('Photo updated successfully!');
        } else {
          // Upload new photo
          formData.append('project_required_photo_id', selectedSlot.id);
          await axiosInstance.post('/photos/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          toast.success('Photo uploaded successfully!');
        }
      } else if (uploadData.existingPhotoId) {
        // Update caption/notes on existing photo without changing image
        const formData = new FormData();
        formData.append('caption', uploadData.caption);
        formData.append('notes', uploadData.notes || '');
        
        await axiosInstance.put(`/photos/${uploadData.existingPhotoId}/edit`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Photo updated successfully!');
      } else {
        // No photo yet - save caption/notes to the slot
        await axiosInstance.put(`/projects/${projectId}/photos/${selectedSlot.id}`, {
          caption: uploadData.caption,
          notes: uploadData.notes || '',
          version: project.version
        });
        toast.success('Caption and notes saved!');
      }
      
      // Close modal first (immediate feedback)
      setUploadModalOpen(false);
      
      // Reset form data
      setUploadData({ 
        file: null, 
        caption: '', 
        notes: '', 
        preview: null, 
        isEdit: false, 
        existingPhotoId: null,
        existingPhotoUrl: null 
      });
      
      // Reload project data in background
      await loadProjectData();
      
      // Force refresh of all photo cards
      setRefreshKey(prev => prev + 1);
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(formatErrorMessage(error, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const [deleteSlotId, setDeleteSlotId] = useState(null);

  const handleClearPhoto = async () => {
    if (!deletePhotoId) return;

    try {
      await axiosInstance.delete(`/photos/by-slot/${deletePhotoId}`);
      toast.success('Photo cleared from slot');
      
      setDeletePhotoId(null);
      
      // Update the upload modal to reflect the cleared state
      setUploadData({
        ...uploadData,
        preview: null,
        existingPhotoUrl: null,
        isEdit: false, // Now it's ready for new upload
      });
      
      // Reload project data to update status and photo counts
      await loadProjectData();
    } catch (error) {
      toast.error('Failed to clear photo');
    }
  };

  const handleDeleteSlot = async () => {
    if (!deleteSlotId) return;

    try {
      await axiosInstance.delete(`/projects/${projectId}/photos/${deleteSlotId}`);
      toast.success('Photo slot deleted');
      setDeleteSlotId(null);
      loadProjectData();
    } catch (error) {
      toast.error('Failed to delete photo slot');
    }
  };

  const handleAddCustomPhoto = async (e) => {
    e.preventDefault();
    if (!customPhotoName.trim()) {
      toast.error('Please enter a photo name');
      return;
    }

    try {
      await axiosInstance.post(`/projects/${projectId}/photos/custom`, {
        project_id: projectId,
        name: customPhotoName,
        category_id: customPhotoCategory || null,
        required: customPhotoRequired,
        enabled: true,
      });
      toast.success('Custom photo slot added');
      setShowAddCustomModal(false);
      setCustomPhotoName('');
      setCustomPhotoCategory('');
      setCustomPhotoRequired(false);
      loadProjectData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to add custom photo'));
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      await axiosInstance.post(`/projects/${projectId}/categories`, {
        name: newCategoryName.trim()
      });
      toast.success('Category added successfully');
      setShowAddCategoryModal(false);
      setNewCategoryName('');
      await loadProjectData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to add category'));
    }
  };

  const handleImportCsvTemplate = async () => {
    if (!selectedCsvTemplateId) {
      toast.error('Please select a CSV template');
      return;
    }

    setImportingCsv(true);
    try {
      const response = await axiosInstance.post(`/projects/${projectId}/import-csv-template`, {
        csv_template_id: selectedCsvTemplateId
      });
      
      toast.success(
        `CSV template imported! ${response.data.new_categories} new categories, ` +
        `${response.data.merged_categories} merged categories, ` +
        `${response.data.new_photo_slots} new photo slots`
      );
      
      setShowImportCsvModal(false);
      setSelectedCsvTemplateId('');
      await loadProjectData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to import CSV template'));
    } finally {
      setImportingCsv(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategoryId) return;

    // Check if confirmation text matches category name
    if (deleteCategoryConfirmText !== deleteCategoryName) {
      toast.error('Category name does not match');
      return;
    }

    try {
      const response = await axiosInstance.delete(`/projects/${projectId}/categories/${deleteCategoryId}`);
      toast.success(`Category deleted (${response.data.deleted_photos} photos removed)`);
      setDeleteCategoryId(null);
      setDeleteCategoryName('');
      setDeleteCategoryConfirmText('');
      await loadProjectData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to delete category'));
    }
  };

  const handleCategoryChange = async (newCategoryId) => {
    if (!selectedSlot) return;

    const categoryIdToSend = newCategoryId === 'uncategorized' ? null : newCategoryId;

    try {
      await axiosInstance.patch(`/projects/${projectId}/photos/${selectedSlot.id}/category`, {
        category_id: categoryIdToSend
      });
      toast.success('Photo slot moved to new category');
      await loadProjectData();
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to move photo slot'));
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = categories.findIndex((cat) => cat.id === active.id);
    const newIndex = categories.findIndex((cat) => cat.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newCategories = arrayMove(categories, oldIndex, newIndex);
    
    // Optimistically update UI
    setCategories(newCategories);

    // Prepare the order updates
    const categoryOrders = newCategories.map((cat, index) => ({
      id: cat.id,
      order: index
    }));

    try {
      await axiosInstance.patch(`/projects/${projectId}/categories/reorder`, categoryOrders);
      toast.success('Categories reordered');
    } catch (error) {
      toast.error('Failed to reorder categories');
      // Revert on error
      await loadProjectData();
    }
  };


  const handleSlotDragOver = (event, categoryId) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    // Get current slots for this category from photos state (already sorted by order)
    const currentPhotos = photos.filter(p => p.category_id === categoryId).sort((a, b) => a.order - b.order);
    
    const oldIndex = currentPhotos.findIndex((slot) => slot.id === active.id);
    const newIndex = currentPhotos.findIndex((slot) => slot.id === over.id);

    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

    // Use arrayMove to reorder
    const reorderedSlots = arrayMove(currentPhotos, oldIndex, newIndex);
    
    // Reassign order values explicitly (0, 1, 2, 3...)
    const slotsWithNewOrder = reorderedSlots.map((slot, index) => ({
      ...slot,
      order: index
    }));
    
    // Update categories state
    const newCategories = categories.map(cat => 
      cat.id === categoryId ? { ...cat, slots: slotsWithNewOrder } : cat
    );
    
    // Update photos state - replace slots for this category
    const otherPhotos = photos.filter(p => p.category_id !== categoryId);
    const updatedPhotos = [...otherPhotos, ...slotsWithNewOrder];
    
    setCategories(newCategories);
    setPhotos(updatedPhotos);
  };

  const handleSlotDragEnd = async (event, categoryId) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    // Get final slots order from the photos state (already updated by onDragOver)
    const categorySlots = photos.filter(p => p.category_id === categoryId).sort((a, b) => a.order - b.order);

    // Prepare the order updates for API - use explicit order values
    const slotOrders = categorySlots.map((slot) => ({
      slot_id: slot.id,
      order: slot.order  // Use the order value from state
    }));

    try {
      await axiosInstance.patch(
        `/projects/${projectId}/categories/${categoryId}/slots/reorder`, 
        slotOrders
      );
      toast.success('Photo order updated');
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Failed to reorder photos');
      // Revert on error
      await loadProjectData();
    }
  };


  const handleDeleteProject = async () => {
    try {
      await axiosInstance.delete(`/projects/${projectId}`);
      toast.success('Project deleted');
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleToggleLock = async () => {
    try {
      const newLockedState = !project.locked;
      
      // If locking the project, turn off filter
      if (newLockedState && filterMissing) {
        setFilterMissing(false);
      }
      
      await axiosInstance.patch(`/projects/${projectId}/lock`, {
        locked: newLockedState
      });
      toast.success(`Project ${newLockedState ? 'locked' : 'unlocked'} successfully`);
      
      // Load data with filter explicitly set to false when locking
      await loadProjectData(newLockedState ? false : null);
    } catch (error) {
      toast.error(`Failed to ${project.locked ? 'unlock' : 'lock'} project`);
    }
  };

  const handleExportClick = () => {
    setShowExportPreview(true);
  };

  const handleExportConfirm = async () => {
    setExporting(true);
    setShowExportPreview(false);
    
    try {
      // Update export settings first
      await axiosInstance.put('/export-settings', exportSettings);
      
      const response = await axiosInstance.post(`/export/${projectId}`);
      const exportId = response.data.export_id;
      
      toast.success(`Export created with ${response.data.export_count} photos. Downloading...`);

      // Download the export
      const downloadUrl = `${API}/export/${exportId}/download`;
      const token = localStorage.getItem('token');

      // Fetch with auth header
      const downloadResponse = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!downloadResponse.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await downloadResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project.name.replace(/[^a-z0-9]/gi, '_')}_export.zip`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      toast.success('Download complete!');
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const openUploadModal = (slot, existingPhotoData = null, existingPhotoUrl = null) => {
    setSelectedSlot(slot);
    
    // Always use slot's caption/notes (caption lives in slot, not photo)
    setUploadData({
      file: null,
      caption: slot.caption || slot.name,
      notes: slot.notes || '',
      preview: null,
      isEdit: true, // Always allow editing caption/notes
      existingPhotoId: existingPhotoData?.id || null,
      existingPhotoUrl: existingPhotoUrl,
    });
    
    // Always open modal first
    setUploadModalOpen(true);
  };

  const handleRequiredToggle = async (checked) => {
    if (!selectedSlot) return;
    
    try {
      await axiosInstance.put(`/projects/${projectId}/photos/${selectedSlot.id}`, {
        required: checked,
        version: project.version,
      });
      
      // Update local state
      setSelectedSlot({ ...selectedSlot, required: checked });
      
      // Reload project data to get updated version and slot info
      await loadProjectData();
      
      toast.success(`Photo marked as ${checked ? 'required' : 'optional'}`);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('Version conflict. Please refresh and try again.');
      } else {
        toast.error('Failed to update required status');
      }
    }
  };

  const handleCategoryNameEdit = (category) => {
    setEditingCategoryId(category.id);
    setEditingCategoryName(category.name);
  };

  const handleCategoryNameSave = async (categoryId) => {
    if (!editingCategoryName.trim()) {
      toast.error('Category name cannot be empty');
      return;
    }

    try {
      await axiosInstance.put(`/projects/${projectId}/categories/${categoryId}`, {
        name: editingCategoryName.trim()
      });
      
      toast.success('Category name updated');
      setEditingCategoryId(null);
      setEditingCategoryName('');
      await loadProjectData();
    } catch (error) {
      toast.error('Failed to update category name');
    }
  };

  const handleCategoryNameCancel = () => {
    setEditingCategoryId(null);
    setEditingCategoryName('');
  };

  // Pre-validate file by attempting to read it (fixes iOS/Android iCloud/large photo issues)
  // Now returns the data URL instead of just true/false
  const preValidateFile = async (file, retryCount = 0) => {
    const MAX_RETRIES = 2;
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        // Successfully read the file - return the data URL
        resolve(reader.result);
      };
      
      reader.onerror = async () => {
        // Failed to read file
        if (retryCount < MAX_RETRIES) {
          // Retry after a short delay
          await new Promise(r => setTimeout(r, 500));
          try {
            const result = await preValidateFile(file, retryCount + 1);
            resolve(result);
          } catch (err) {
            reject(err);
          }
        } else {
          reject(new Error('Failed to load photo. The file may still be downloading from iCloud or your device storage.'));
        }
      };
      
      // Start reading the file
      reader.readAsDataURL(file);
    });
  };

  const handleChangePicture = () => {
    // Simple file input - works reliably on iOS
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      // Validate file size first
      if (file.size > 20 * 1024 * 1024) {
        toast.error('File too large (max 20MB)');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Show loading state
      setLoadingPhoto(true);
      setPhotoLoadError(null);
      
      try {
        // Pre-validate and read the file (important for iOS/Android iCloud photos)
        // This returns the data URL so we don't have to read it again
        const dataUrl = await preValidateFile(file);
        
        // File is valid and readable, save both file and pre-loaded data URL
        setSelectedFile(file);
        setSelectedImageDataUrl(dataUrl); // Save the pre-loaded image
        setCropModalOpen(true);
        setLoadingPhoto(false);
      } catch (error) {
        // Failed to load/read the file
        console.error('Photo validation error:', error);
        setPhotoLoadError(error.message || 'Failed to load photo. Please try again.');
        setLoadingPhoto(false);
        toast.error(error.message || 'Failed to load photo. Please try again.');
      }
    };
    
    input.click();
  };

  const handleCropComplete = async (croppedFile) => {
    // After cropping, set the file and open/reopen upload modal
    setUploadData(prev => ({
      ...prev,
      file: croppedFile,
      preview: URL.createObjectURL(croppedFile),
    }));
    setUploadModalOpen(true);
  };

  const getPhotosByCategory = (categoryId) => {
    return photos.filter((p) => p.category_id === categoryId);
  };

  const getUncategorizedPhotos = () => {
    return photos.filter((p) => !p.category_id);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Header */}
          <div className="flex md:hidden justify-between items-center py-3 gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-button" size="sm">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-bold text-gray-900 truncate">{project.name}</h1>
                  {project.locked ? (
                    <Lock className="w-4 h-4 text-gray-500 flex-shrink-0" title="Project is locked" />
                  ) : (
                    <Unlock className="w-4 h-4 text-gray-400 flex-shrink-0" title="Project is unlocked" />
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {project.customer} • {new Date(project.date).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {user.role === 'admin' && (
                <Button
                  onClick={() => navigate('/admin')}
                  variant="outline"
                  size="sm"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="logout-button" className="flex items-center gap-2 text-sm" size="sm">
                    <LogOut className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Logout</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to logout?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onLogout}>Logout</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
          
          {/* Desktop Header */}
          <div className="hidden md:grid grid-cols-3 items-center py-3 gap-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-button-desktop" size="sm">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-900">{project.name}</h1>
                  {project.locked ? (
                    <Lock className="w-5 h-5 text-gray-500 flex-shrink-0" title="Project is locked" />
                  ) : (
                    <Unlock className="w-5 h-5 text-gray-400 flex-shrink-0" title="Project is unlocked" />
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {project.customer} • {new Date(project.date).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_admin-user-portal-2/artifacts/9fookd3l_Logo%20Datastorm%20Blue%20with%20Transparent%20BG%20Cropped.png" 
                alt="Datastorm Logo" 
                className="h-16 w-auto"
              />
            </div>
            <div className="flex items-center gap-2 justify-end">
              {user.role === 'admin' && (
                <Button
                  onClick={() => navigate('/admin')}
                  variant="outline"
                  className="flex items-center gap-2 text-sm"
                  size="sm"
                >
                  <Settings className="w-4 h-4" />
                  <span className="hidden md:inline">Admin</span>
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="logout-button-desktop" className="flex items-center gap-2 text-sm" size="sm">
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Logout</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to logout?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onLogout}>Logout</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Hide filter when project is locked */}
            {!project?.locked && (
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0">
                  <Switch
                    checked={filterMissing}
                    onCheckedChange={setFilterMissing}
                    data-testid="filter-missing-toggle"
                    className="scale-100"
                  />
                </div>
                <Label className="text-sm font-medium cursor-pointer" onClick={() => setFilterMissing(!filterMissing)}>
                  Show Missing Required Only
                </Label>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 border rounded-md p-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => {
                setViewMode('grid');
                localStorage.setItem('projectDetailViewMode', 'grid');
              }}
              className="h-7 w-7 p-0"
              style={{ minHeight: '28px', minWidth: '28px' }}
            >
              <Grid3x3 className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => {
                setViewMode('list');
                localStorage.setItem('projectDetailViewMode', 'list');
              }}
              className="h-7 w-7 p-0"
              style={{ minHeight: '28px', minWidth: '28px' }}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Action Buttons Sub-Header */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
          {/* Left aligned buttons */}
          <div className="flex items-center gap-2">
            {/* Hide Add button when locked */}
            {!project?.locked && (
              <Button
                variant="outline"
                onClick={() => setShowAddSelectionModal(true)}
                data-testid="add-button"
                className="flex items-center gap-2"
                size="sm"
                aria-label="Add"
                title="Add Photo Slot or Category"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden md:inline">Add</span>
              </Button>
            )}
            {/* Import CSV Template button - available to all users */}
            {!project?.locked && (
              <Button
                variant="outline"
                onClick={() => setShowImportCsvModal(true)}
                data-testid="import-csv-button"
                className="flex items-center gap-2"
                size="sm"
                aria-label="Import CSV Template"
                title="Import CSV Template"
              >
                <FileText className="w-4 h-4" />
                <span className="hidden md:inline">Import CSV</span>
              </Button>
            )}
            <Button
              onClick={handleExportClick}
              disabled={exporting}
              data-testid="export-button"
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white flex items-center gap-2"
              size="sm"
              aria-label={exporting ? 'Exporting...' : 'Export Photos'}
              title={exporting ? 'Exporting Photos...' : 'Export Photos'}
            >
              <Download className="w-4 h-4" />
              {exporting ? (
                <span className="hidden md:inline">Exporting...</span>
              ) : (
                <span className="hidden md:inline">Export Photos</span>
              )}
            </Button>
          </div>
          
          {/* Right aligned buttons */}
          <div className="flex items-center gap-2">
            {/* Hide Edit and Delete when project is locked */}
            {!project?.locked && (
              <>
                <Button
                  onClick={handleEditProject}
                  variant="outline"
                  data-testid="edit-project-button"
                  size="sm"
                  className="flex items-center gap-2"
                  aria-label="Edit Project"
                  title="Edit Project"
                >
                  <Edit2 className="w-4 h-4" />
                  <span className="hidden md:inline">Edit Project</span>
                </Button>
                {user.role === 'admin' && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="delete-project-button"
                        className="flex items-center gap-2 text-red-600 hover:text-red-700"
                        size="sm"
                        aria-label="Delete Project"
                        title="Delete Project"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden md:inline">Delete Project</span>
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Project?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{project.name}"? This will permanently delete all photos and data. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteProject} className="bg-red-600 hover:bg-red-700">
                          Delete Project
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </>
            )}
            {/* Lock/Unlock button - always visible for admin, positioned after Edit/Delete */}
            {user.role === 'admin' && (
              <Button
                onClick={handleToggleLock}
                variant="outline"
                data-testid="lock-project-button"
                className="flex items-center gap-2"
                size="sm"
                aria-label={project.locked ? 'Unlock Project' : 'Lock Project'}
                title={project.locked ? 'Unlock Project' : 'Lock Project'}
              >
                {project.locked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                <span className="hidden md:inline">{project.locked ? 'Unlock Project' : 'Lock Project'}</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Required Photos Status Banner - Hide when locked */}
        {!project?.locked && project && typeof project.remaining_required_photos === 'number' && (
          <div className={`mb-6 px-6 py-4 rounded-lg border-2 ${
            project.remaining_required_photos === 0 
              ? 'bg-green-50 border-green-500 text-green-800'
              : 'bg-blue-50 border-blue-500 text-blue-800'
          }`}>
            <div className="flex items-center gap-3">
              {project.remaining_required_photos === 0 ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  <p className="font-semibold text-base">All Required Photos Uploaded!</p>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5" />
                  <p className="font-semibold text-base">
                    ({project.remaining_required_photos}) Required Photo{project.remaining_required_photos > 1 ? 's' : ''} Remaining
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {photos.length === 0 && filterMissing && (
          <Card className="text-center py-12" data-testid="no-missing-photos">
            <CardContent>
              <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">All Required Photos Complete!</h3>
              <p className="text-gray-600">No missing required photos</p>
            </CardContent>
          </Card>
        )}

        {categories.length > 0 ? (
          // Only allow category reordering for admins when project is not locked
          user.role === 'admin' && !project?.locked ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={categories.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <Accordion type="multiple" defaultValue={categories.map((c) => c.id)} className="space-y-4">
                  {categories
                    .filter((category) => {
                      // When filterMissing is on AND project is unlocked, hide categories with no photos
                      if (filterMissing && !project?.locked) {
                        return getPhotosByCategory(category.id).length > 0;
                      }
                      return true;
                    })
                    .map((category) => (
                    <SortableCategoryItem
                      key={category.id}
                      category={category}
                      isAdmin={user.role === 'admin'}
                      getPhotosByCategory={getPhotosByCategory}
                      editingCategoryId={editingCategoryId}
                      editingCategoryName={editingCategoryName}
                      setEditingCategoryName={setEditingCategoryName}
                      handleCategoryNameSave={handleCategoryNameSave}
                      handleCategoryNameCancel={handleCategoryNameCancel}
                      user={user}
                      handleCategoryNameEdit={handleCategoryNameEdit}
                      setDeleteCategoryId={setDeleteCategoryId}
                      setDeleteCategoryName={setDeleteCategoryName}
                      viewMode={viewMode}
                      openUploadModal={openUploadModal}
                      setDeletePhotoId={setDeletePhotoId}
                      setDeleteSlotId={setDeleteSlotId}
                      refreshKey={refreshKey}
                      projectLocked={project?.locked || false}
                      categories={categories}
                      sensors={sensors}
                      handleSlotDragEnd={handleSlotDragEnd}
                      handleSlotDragOver={handleSlotDragOver}
                      setShowAddCustomModal={setShowAddCustomModal}
                      setCustomPhotoCategory={setCustomPhotoCategory}
                    />
                  ))}
                </Accordion>
              </SortableContext>
            </DndContext>
          ) : (
            // When locked or not admin, render categories without drag-and-drop
            <Accordion type="multiple" defaultValue={categories.map((c) => c.id)} className="space-y-4">
              {categories
                .filter((category) => {
                  // When filterMissing is on AND project is unlocked, hide categories with no photos
                  if (filterMissing && !project?.locked) {
                    return getPhotosByCategory(category.id).length > 0;
                  }
                  return true;
                })
                .map((category) => (
                <SortableCategoryItem
                  key={category.id}
                  category={category}
                  isAdmin={user.role === 'admin'}
                  getPhotosByCategory={getPhotosByCategory}
                  editingCategoryId={editingCategoryId}
                  editingCategoryName={editingCategoryName}
                  setEditingCategoryName={setEditingCategoryName}
                  handleCategoryNameSave={handleCategoryNameSave}
                  handleCategoryNameCancel={handleCategoryNameCancel}
                  user={user}
                  handleCategoryNameEdit={handleCategoryNameEdit}
                  setDeleteCategoryId={setDeleteCategoryId}
                  setDeleteCategoryName={setDeleteCategoryName}
                  viewMode={viewMode}
                  openUploadModal={openUploadModal}
                  setDeletePhotoId={setDeletePhotoId}
                  setDeleteSlotId={setDeleteSlotId}
                  refreshKey={refreshKey}
                  projectLocked={project?.locked || false}
                  categories={categories}
                  sensors={sensors}
                  handleSlotDragEnd={handleSlotDragEnd}
                  handleSlotDragOver={handleSlotDragOver}
                  setShowAddCustomModal={setShowAddCustomModal}
                  setCustomPhotoCategory={setCustomPhotoCategory}
                />
              ))}
            </Accordion>
          )
        ) : null}

        {/* Uncategorized */}
        {getUncategorizedPhotos().length > 0 && (
          <Accordion type="single" collapsible defaultValue="uncategorized" className="mt-6">
            <AccordionItem value="uncategorized" className="bg-white rounded-lg shadow-sm border border-gray-200">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <h3 className="text-lg font-semibold text-gray-900">Uncategorized ({getUncategorizedPhotos().length})</h3>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className={viewMode === 'grid' ? 'photo-grid' : 'space-y-2'}>
                  {getUncategorizedPhotos().map((slot) => (
                    <PhotoSlotCard
                      key={slot.id}
                      slot={slot}
                      onUpload={openUploadModal}
                      onDeleteClick={setDeletePhotoId}
                      onDeleteSlot={setDeleteSlotId}
                      user={user}
                      refreshKey={refreshKey}
                      viewMode={viewMode}
                      projectLocked={project?.locked || false}
                      categories={categories}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>

      {/* Upload Modal */}
      {/* Upload/Update Photo Modal */}
      <ResponsiveModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        title="Update Photo"
        maxWidth="sm:max-w-xl"
        mobileHeight="90vh"
      >
        <form onSubmit={handleUpload} className="space-y-4 pb-4">
          {/* Preview Section */}
          <div className="space-y-2">
            <Label className="text-base">Photo Preview</Label>
            {uploadData.preview || uploadData.existingPhotoUrl ? (
              <div className="flex justify-center bg-gray-100 rounded-lg p-4 touch-none">
                <div className="relative" style={{ width: '225px', aspectRatio: '3/4' }}>
                  <img 
                    src={uploadData.preview || uploadData.existingPhotoUrl} 
                    alt="Preview"
                    className="w-full h-full object-cover rounded pointer-events-none select-none"
                  />
                </div>
              </div>
            ) : (
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Camera className="w-16 h-16 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">No photo selected</p>
              </div>
            )}
          </div>

          {/* Change Picture Button */}
          <div className="space-y-2">
            <ResponsiveButton
              type="button"
              variant="outline"
              onClick={handleChangePicture}
              className="w-full"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadData.isEdit ? 'Change Picture' : 'Select Picture'}
            </ResponsiveButton>
            <p className="text-xs md:text-sm text-gray-500 text-center">Portrait 3:4 ratio • Max 20MB</p>
          </div>

          {/* Caption */}
          <div className="space-y-2">
            <Label htmlFor="caption" className="text-base">Caption</Label>
            <Textarea
              id="caption"
              data-testid="caption-input"
              placeholder="Photo caption"
              value={uploadData.caption}
              onChange={(e) => setUploadData({ ...uploadData, caption: e.target.value })}
              rows={2}
              maxLength={500}
              className="min-h-[48px] text-base"
            />
            <p className="text-xs md:text-sm text-gray-500">Burned into exported image</p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-base">Notes (Optional)</Label>
            <Textarea
              id="notes"
              data-testid="notes-input"
              placeholder="Internal notes..."
              value={uploadData.notes}
              onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
              rows={3}
              maxLength={1000}
              className="min-h-[48px] text-base"
            />
            <p className="text-xs md:text-sm text-gray-500">Not visible in exports</p>
          </div>

          {/* Category Selector (Admin Only) */}
          {user.role === 'admin' && uploadData.isEdit && (
            <div className="space-y-2">
              <Label htmlFor="category-select">Move to Category</Label>
              <Select 
                value={selectedSlot?.category_id || 'uncategorized'} 
                onValueChange={(value) => handleCategoryChange(value)}
              >
                <SelectTrigger id="category-select" className="text-base">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="uncategorized">Uncategorized</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Move this photo slot to a different category</p>
            </div>
          )}

          {/* Required Toggle (Admin Only) */}
          {user.role === 'admin' && (
            <div className="flex items-center justify-between py-2 px-4 bg-gray-50 rounded-lg">
              <Label htmlFor="required-toggle" className="cursor-pointer">
                Mark as Required
              </Label>
              <Switch
                id="required-toggle"
                checked={selectedSlot?.required || false}
                onCheckedChange={handleRequiredToggle}
              />
            </div>
          )}

          {/* Main Action Button */}
          <ResponsiveButton 
            type="submit" 
            data-testid="upload-submit-button" 
            className="w-full bg-blue-500 hover:bg-blue-600" 
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {uploadData.isEdit ? 'Saving...' : 'Uploading...'}
              </>
            ) : uploadData.isEdit ? (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload Photo
              </>
            )}
          </ResponsiveButton>

          {/* Additional Actions */}
          {(uploadData.isEdit || (user.role === 'admin' || (!selectedSlot?.source_template_item_id && selectedSlot?.created_by === user.id))) && (
            <div className="pt-2 border-t space-y-2">
              {/* Clear Photo button - only show if photo exists */}
              {uploadData.isEdit && (uploadData.existingPhotoUrl || selectedSlot?.has_photo) && (
                <ResponsiveButton
                  type="button"
                  variant="outline"
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => {
                    setDeletePhotoId(selectedSlot.id);
                  }}
                  disabled={project?.locked && user.role !== 'admin'}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Photo
                </ResponsiveButton>
              )}
              
              {/* Delete Slot button - show for admins or slot creators */}
              {(user.role === 'admin' || (!selectedSlot?.source_template_item_id && selectedSlot?.created_by === user.id)) && (
                <ResponsiveButton
                  type="button"
                  variant="outline"
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => {
                    setUploadModalOpen(false);
                    setDeleteSlotId(selectedSlot.id);
                  }}
                  disabled={project?.locked && user.role !== 'admin'}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Slot
                </ResponsiveButton>
              )}
            </div>
          )}
        </form>
      </ResponsiveModal>

      {/* Add Selection Modal */}
      <Dialog open={showAddSelectionModal} onOpenChange={setShowAddSelectionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>What would you like to add?</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            <Button
              onClick={() => {
                setShowAddSelectionModal(false);
                setShowAddCustomModal(true);
              }}
              className="w-full justify-start h-auto py-4 px-6"
              variant="outline"
            >
              <div className="flex items-center gap-3">
                <Camera className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">Photo Slot</div>
                  <div className="text-sm text-muted-foreground">Add a new photo slot to this project</div>
                </div>
              </div>
            </Button>
            <Button
              onClick={() => {
                setShowAddSelectionModal(false);
                setShowAddCategoryModal(true);
              }}
              className="w-full justify-start h-auto py-4 px-6"
              variant="outline"
            >
              <div className="flex items-center gap-3">
                <FolderTree className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">Category</div>
                  <div className="text-sm text-muted-foreground">Add a new category to organize photos</div>
                </div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Category Modal */}
      <ResponsiveModal
        open={showAddCategoryModal}
        onOpenChange={setShowAddCategoryModal}
        title="Add New Category"
        maxWidth="sm:max-w-md"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleAddCategory(); }} className="space-y-4 pb-4">
          <div className="space-y-2">
            <Label htmlFor="category-name">Category Name *</Label>
            <Input
              id="category-name"
              data-testid="category-name-input"
              placeholder="e.g., Interior Shots"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              required
              autoComplete="off"
              className="text-base"
            />
          </div>
          <ResponsiveButton type="submit" data-testid="add-category-submit" className="w-full">
            Add Category
          </ResponsiveButton>
        </form>
      </ResponsiveModal>

      {/* Import CSV Template Modal */}
      <ResponsiveModal
        open={showImportCsvModal}
        onOpenChange={setShowImportCsvModal}
        title="Import CSV Template"
        maxWidth="sm:max-w-md"
      >
        <div className="space-y-4 pb-4">
          <p className="text-sm text-gray-600">
            Import categories and photo slots from a CSV template. Categories with the same name will be merged.
          </p>
          <div className="space-y-2">
            <Label htmlFor="csv-template-select">Select CSV Template *</Label>
            <select
              id="csv-template-select"
              value={selectedCsvTemplateId}
              onChange={(e) => setSelectedCsvTemplateId(e.target.value)}
              className="w-full border rounded-md p-2 text-base"
              required
            >
              <option value="">Select a CSV template...</option>
              {csvTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.category_count} categories, {template.slot_count} slots)
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-2 pt-2">
            <ResponsiveButton 
              onClick={handleImportCsvTemplate}
              disabled={!selectedCsvTemplateId || importingCsv}
              className="w-full"
            >
              {importingCsv ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                'Import Template'
              )}
            </ResponsiveButton>
            <ResponsiveButton
              type="button"
              variant="outline"
              onClick={() => {
                setShowImportCsvModal(false);
                setSelectedCsvTemplateId('');
              }}
              disabled={importingCsv}
              className="w-full"
            >
              Cancel
            </ResponsiveButton>
          </div>
        </div>
      </ResponsiveModal>

      {/* Add Custom Photo Modal */}
      <ResponsiveModal
        open={showAddCustomModal}
        onOpenChange={setShowAddCustomModal}
        title="Add Custom Photo Slot"
        maxWidth="sm:max-w-md"
      >
        <form onSubmit={handleAddCustomPhoto} className="space-y-4 pb-4">
          <div className="space-y-2">
            <Label htmlFor="custom-name">Photo Name *</Label>
            <Input
              id="custom-name"
              data-testid="custom-photo-name-input"
              placeholder="e.g., Additional View"
              value={customPhotoName}
              onChange={(e) => setCustomPhotoName(e.target.value)}
              required
              autoComplete="off"
              className="text-base" 
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="custom-category">Category (optional)</Label>
            <Select value={customPhotoCategory || "none"} onValueChange={(val) => setCustomPhotoCategory(val === "none" ? "" : val)}>
              <SelectTrigger data-testid="custom-category-select" className="text-base">
                <SelectValue placeholder="Select a category..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No category</SelectItem>
                {categories && categories.length > 0 && categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {user.role === 'admin' && (
            <div className="flex items-center justify-between">
              <Label htmlFor="custom-required">Mark as Required</Label>
              <Switch
                id="custom-required"
                checked={customPhotoRequired}
                onCheckedChange={setCustomPhotoRequired}
                data-testid="custom-required-switch"
              />
            </div>
          )}
          <ResponsiveButton type="submit" data-testid="add-custom-submit" className="w-full">
            Add Photo Slot
          </ResponsiveButton>
        </form>
      </ResponsiveModal>

      {/* Clear Photo Confirmation */}
      <AlertDialog open={!!deletePhotoId} onOpenChange={() => setDeletePhotoId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Photo?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the photo but keep the photo slot. You can upload a new photo to this slot later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearPhoto} className="bg-red-600 hover:bg-red-700">
              Clear Photo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Slot Confirmation */}
      <AlertDialog open={!!deleteSlotId} onOpenChange={() => setDeleteSlotId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Photo Slot?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this photo slot? This will remove the slot and any uploaded photo. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSlot} className="bg-red-600 hover:bg-red-700">
              Delete Slot
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Category Modal */}
      <AlertDialog open={!!deleteCategoryId} onOpenChange={() => {
        setDeleteCategoryId(null);
        setDeleteCategoryName('');
        setDeleteCategoryConfirmText('');
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete <strong>{deleteCategoryName}</strong>? 
              </p>
              <p className="text-red-600 font-semibold">
                ⚠️ Deleting this category will also delete all photo slots inside. This action cannot be undone.
              </p>
              <div className="pt-2">
                <Label htmlFor="delete-confirm" className="text-sm font-medium">
                  Type "{deleteCategoryName}" to confirm:
                </Label>
                <Input
                  id="delete-confirm"
                  value={deleteCategoryConfirmText}
                  onChange={(e) => setDeleteCategoryConfirmText(e.target.value)}
                  placeholder={deleteCategoryName}
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteCategoryConfirmText('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteCategory} 
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteCategoryConfirmText !== deleteCategoryName}
            >
              Delete Category
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Project Modal */}
      <ResponsiveModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        title="Edit Project Details"
        maxWidth="sm:max-w-lg"
        mobileHeight="90vh"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4 pb-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Project Name *</Label>
            <Input
              id="edit-name"
              data-testid="edit-project-name"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              required
              className="text-base"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-customer">Customer</Label>
            <Input
              id="edit-customer"
              data-testid="edit-project-customer"
              value={editForm.customer}
              onChange={(e) => setEditForm({ ...editForm, customer: e.target.value })}
              placeholder="Customer name (optional)"
              className="text-base"
            />
          </div>
          <ResponsiveButton type="submit" data-testid="save-project-button" className="w-full">
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </ResponsiveButton>
        </form>
      </ResponsiveModal>

      {/* Export Preview Modal */}
      <Dialog open={showExportPreview} onOpenChange={setShowExportPreview}>
        <DialogContent className="max-w-md p-6">
          <DialogHeader>
            <DialogTitle>Export Photos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-3">
              <h3 className="font-semibold text-sm">Burn-in Options</h3>
              <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between gap-4">
                  <Label htmlFor="export-project-name" className="cursor-pointer text-sm">
                    Include Project Name
                  </Label>
                  <Switch
                    id="export-project-name"
                    checked={exportSettings.burn_in_project_name}
                    onCheckedChange={(checked) => setExportSettings({...exportSettings, burn_in_project_name: checked})}
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <Label htmlFor="export-timestamp" className="cursor-pointer text-sm">
                    Include Date & Time
                  </Label>
                  <Switch
                    id="export-timestamp"
                    checked={exportSettings.burn_in_timestamp}
                    onCheckedChange={(checked) => setExportSettings({...exportSettings, burn_in_timestamp: checked})}
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <Label htmlFor="export-caption" className="cursor-pointer text-sm">
                    Include Caption
                  </Label>
                  <Switch
                    id="export-caption"
                    checked={exportSettings.caption_outside_with_bg}
                    onCheckedChange={(checked) => setExportSettings({...exportSettings, caption_outside_with_bg: checked})}
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <Label htmlFor="export-category-caption" className="cursor-pointer text-sm">
                    Include Category
                  </Label>
                  <Switch
                    id="export-category-caption"
                    checked={exportSettings.prepend_category_to_caption}
                    onCheckedChange={(checked) => setExportSettings({...exportSettings, prepend_category_to_caption: checked})}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Preview Example</h3>
              <div className="bg-gray-800 p-3 rounded-lg">
                <div className="bg-white rounded-lg overflow-hidden mx-auto shadow-lg" style={{ maxWidth: '240px' }}>
                  {exportSettings.burn_in_project_name || exportSettings.burn_in_timestamp ? (
                    <div className="bg-black text-white px-2 py-1 flex justify-between text-xs">
                      {exportSettings.burn_in_project_name && <span className="truncate">{project.name}</span>}
                      {exportSettings.burn_in_timestamp && <span className="text-right">{new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</span>}
                    </div>
                  ) : null}
                  <div className="bg-gradient-to-br from-blue-200 to-blue-300 flex items-center justify-center" style={{ aspectRatio: '3/4' }}>
                    <Camera className="w-10 h-10 text-blue-600 opacity-50" />
                  </div>
                  {exportSettings.caption_outside_with_bg && (
                    <div className="bg-black text-white px-2 py-1 text-xs">
                      {exportSettings.prepend_category_to_caption ? 'Category: Photo caption' : 'Photo caption appears here'}
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-600 text-center">
                Example showing how burn-ins will appear
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowExportPreview(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleExportConfirm}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Loading Modal - for iOS/Android file validation */}
      <Dialog open={loadingPhoto} onOpenChange={() => {
        // Prevent closing during validation, only allow close on error via button
        if (photoLoadError) {
          setLoadingPhoto(false);
          setPhotoLoadError(null);
        }
      }}>
        <DialogContent className="max-w-sm">
          <div className="flex flex-col items-center justify-center py-6 space-y-4">
            {!photoLoadError ? (
              <>
                <Loader2 className="w-16 h-16 animate-spin text-blue-500" />
                <div className="text-center space-y-2">
                  <p className="text-lg font-medium">Loading Photo...</p>
                  <p className="text-sm text-gray-500">
                    Please wait while your photo is being prepared.
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    This may take a moment for photos stored in iCloud or large files.
                  </p>
                </div>
              </>
            ) : (
              <div className="w-full space-y-3">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 text-center">{photoLoadError}</p>
                </div>
                <Button
                  onClick={() => {
                    setLoadingPhoto(false);
                    setPhotoLoadError(null);
                  }}
                  className="w-full min-h-[48px]"
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Portrait Crop Modal */}
      <PortraitCropModal
        isOpen={cropModalOpen}
        onClose={() => {
          setCropModalOpen(false);
          setSelectedFile(null);
          setSelectedImageDataUrl(null); // Clear pre-loaded image
        }}
        onCropComplete={handleCropComplete}
        initialFile={selectedFile}
        preLoadedImageSrc={selectedImageDataUrl} // Pass pre-loaded image data URL
      />

    </div>
  );
}

// Sortable Category Item Component for drag-and-drop
function SortableCategoryItem({ category, children, isAdmin, getPhotosByCategory, editingCategoryId, editingCategoryName, setEditingCategoryName, handleCategoryNameSave, handleCategoryNameCancel, user, handleCategoryNameEdit, setDeleteCategoryId, setDeleteCategoryName, viewMode, openUploadModal, setDeletePhotoId, setDeleteSlotId, refreshKey, projectLocked, categories, sensors, handleSlotDragEnd, handleSlotDragOver, setShowAddCustomModal, setCustomPhotoCategory }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id, disabled: !isAdmin });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Use category.slots if available (after reordering), otherwise use getPhotosByCategory
  // Then sort by order field
  const categoryPhotos = (category.slots || getPhotosByCategory(category.id)).sort((a, b) => a.order - b.order);

  return (
    <div ref={setNodeRef} style={style}>
      <AccordionItem value={category.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
        <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid={`category-${category.id}`}>
          <div className="flex items-center gap-3 flex-1">
            {user.role === 'admin' && (
              <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing hidden md:block">
                <GripVertical className="w-4 h-4 text-gray-400" />
              </div>
            )}
            {editingCategoryId === category.id ? (
              <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={editingCategoryName}
                  onChange={(e) => setEditingCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCategoryNameSave(category.id);
                    } else if (e.key === 'Escape') {
                      handleCategoryNameCancel();
                    }
                  }}
                  className="text-lg font-semibold"
                  autoFocus
                />
                <Button
                  size="sm"
                  onClick={() => handleCategoryNameSave(category.id)}
                  className="flex-shrink-0"
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCategoryNameCancel}
                  className="flex-shrink-0"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                {(() => {
                  // Calculate required photo stats
                  const requiredSlots = categoryPhotos.filter(p => p.required);
                  const filledRequired = requiredSlots.filter(p => p.has_photo).length;
                  const totalRequired = requiredSlots.length;
                  
                  return (
                    <h3 className="text-lg font-semibold text-gray-900">
                      {category.name} {totalRequired > 0 ? `(${filledRequired}/${totalRequired})` : `(${categoryPhotos.length})`}
                    </h3>
                  );
                })()}
                {/* Show edit/delete buttons only when project is unlocked */}
                {!projectLocked && (
                  <div className="flex items-center gap-1">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryNameEdit(category);
                      }}
                      className="h-6 w-6 p-0 inline-flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                      title="Edit category name"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          handleCategoryNameEdit(category);
                        }
                      }}
                    >
                      <Edit2 className="w-3 h-3" />
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteCategoryId(category.id);
                        setDeleteCategoryName(category.name);
                      }}
                      className="h-6 w-6 p-0 inline-flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer transition-colors text-red-600 hover:text-red-700"
                      title="Delete category"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          setDeleteCategoryId(category.id);
                          setDeleteCategoryName(category.name);
                        }
                      }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-6 pb-6">
          {categoryPhotos.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Camera className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm">No photo slots in this category yet</p>
              <p className="text-xs mt-1">Click "Add" button above to add a photo slot</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragOver={(event) => handleSlotDragOver(event, category.id)}
              onDragEnd={(event) => handleSlotDragEnd(event, category.id)}
            >
              <SortableContext
                items={categoryPhotos.map(s => s.id)}
                strategy={viewMode === 'grid' ? rectSortingStrategy : verticalListSortingStrategy}
              >
                <div className={viewMode === 'grid' ? 'photo-grid' : 'space-y-2'}>
                  {categoryPhotos.map((slot) => (
                    <SortablePhotoSlot
                      key={slot.id}
                      slot={slot}
                      onUpload={openUploadModal}
                      onDeleteClick={setDeletePhotoId}
                      onDeleteSlot={setDeleteSlotId}
                      user={user}
                      refreshKey={refreshKey}
                      viewMode={viewMode}
                      projectLocked={projectLocked}
                      categories={categories}
                    />
                  ))}
                  
                  {/* Phantom slot placeholder - only show when not locked */}
                  {!projectLocked && (
                    <div
                      onClick={() => {
                        setCustomPhotoCategory(category.id);
                        setShowAddCustomModal(true);
                      }}
                      className={`
                        ${viewMode === 'grid' 
                          ? 'aspect-[3/4] min-h-[200px]' 
                          : 'h-[80px]'
                        }
                        border-2 border-dashed border-gray-300 rounded-lg
                        flex flex-col items-center justify-center gap-2
                        cursor-pointer hover:border-blue-500 hover:bg-blue-50
                        transition-all duration-200 group
                      `}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setCustomPhotoCategory(category.id);
                          setShowAddCustomModal(true);
                        }
                      }}
                      title="Add new photo slot to this category"
                    >
                      <Plus className="w-8 h-8 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      <span className="text-sm text-gray-500 group-hover:text-blue-600 transition-colors font-medium">
                        Add Slot
                      </span>
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </AccordionContent>
      </AccordionItem>
    </div>
  );
}


// Sortable wrapper for PhotoSlotCard
function SortablePhotoSlot({ slot, projectLocked, ...props }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: slot.id, 
    disabled: projectLocked // Disable dragging if project is locked
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={`relative ${!projectLocked ? 'group' : ''}`}>
      {/* Photo Slot Card */}
      <PhotoSlotCard 
        slot={slot} 
        projectLocked={projectLocked}
        {...props} 
      />
      
      {/* Drag Handle - overlayed on top right corner, hidden on mobile, visible on hover on desktop */}
      {!projectLocked && (
        <div 
          {...attributes} 
          {...listeners}
          className="hidden md:block absolute top-2 right-2 z-20 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 touch-manipulation transition-opacity bg-white/30 backdrop-blur-sm rounded-md p-1 hover:bg-white/50"
          style={{ touchAction: 'none' }}
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4 text-gray-700 hover:text-gray-900" />
        </div>
      )}
    </div>
  );
}


function PhotoSlotCard({ slot, onUpload, onDeleteClick, onDeleteSlot, user, refreshKey, viewMode, projectLocked, categories }) {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoData, setPhotoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [fullSizeUrl, setFullSizeUrl] = useState(null);

  useEffect(() => {
    if (slot.has_photo) {
      loadPhotoThumbnail();
    } else {
      // Clear photo URL if photo was removed
      if (photoUrl) {
        URL.revokeObjectURL(photoUrl);
        setPhotoUrl(null);
      }
      setPhotoData(null);
      setFullSizeUrl(null);
    }
  }, [slot.has_photo, slot.id, refreshKey]);

  const loadPhotoThumbnail = async () => {
    setLoading(true);
    try {
      // Add cache busting with timestamp
      const response = await axiosInstance.get(`/photos/by-slot/${slot.id}?_t=${Date.now()}`);
      setPhotoData(response.data);
      const photoId = response.data.id;
      
      // Clean up old blob URL to prevent memory leak
      if (photoUrl) {
        URL.revokeObjectURL(photoUrl);
      }
      
      // Fetch thumb variant (300×400) for card display
      const imgResponse = await axiosInstance.get(`/photos/${photoId}/variant/thumb?_t=${Date.now()}`, {
        responseType: 'blob'
      });
      const imageUrl = URL.createObjectURL(imgResponse.data);
      setPhotoUrl(imageUrl);
    } catch (error) {
      console.error('Failed to load photo:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handlePhotoClick = async () => {
    if (!slot.has_photo || !photoData) return;
    
    setShowFullPreview(true);
    
    // Load medium variant (600×800) for preview
    if (!fullSizeUrl) {
      try {
        const imgResponse = await axiosInstance.get(`/photos/${photoData.id}/variant/medium`, {
          responseType: 'blob'
        });
        const imageUrl = URL.createObjectURL(imgResponse.data);
        setFullSizeUrl(imageUrl);
      } catch (error) {
        console.error('Failed to load medium photo');
      }
    }
  };

  const handleDownloadPhoto = async () => {
    if (!photoData) return;
    
    try {
      // Download the large (full-size) variant
      const response = await axiosInstance.get(`/photos/${photoData.id}/variant/large`, {
        responseType: 'blob'
      });
      
      // Get category name
      let categoryName = 'Uncategorized';
      if (slot.category_id) {
        const category = categories.find(c => c.id === slot.category_id);
        if (category) {
          categoryName = category.name;
        }
      }
      
      // Use same naming as export: "Category Name - Caption.jpg"
      const caption = slot.caption || slot.name;
      // Sanitize filename - remove special characters
      const sanitizedCategory = categoryName.replace(/[^a-zA-Z0-9\s-]/g, '');
      const sanitizedCaption = caption.replace(/[^a-zA-Z0-9\s-]/g, '');
      const filename = `${sanitizedCategory}-${sanitizedCaption}.jpg`;
      
      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Photo downloaded');
    } catch (error) {
      console.error('Download error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to download photo';
      toast.error(errorMsg);
    }
  };

  return (
    <>
      <ResponsiveModal
        open={showFullPreview}
        onOpenChange={setShowFullPreview}
        title="Photo Preview"
        maxWidth="sm:max-w-xl"
        mobileHeight="90vh"
      >
        <div className="space-y-4">
          <div className="relative overflow-hidden rounded-lg" style={{ aspectRatio: '3/4' }}>
            {fullSizeUrl ? (
              <img src={fullSizeUrl} alt={slot.name} className="w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            )}
          </div>
          {photoData && (
            <div className="pt-2 space-y-3">
              {slot.caption && (
                <p className="text-lg font-bold text-gray-900 text-center">{slot.caption}</p>
              )}
              <div className="text-sm md:text-base text-gray-600 space-y-1 text-left">
                {photoData.uploader_name && (
                  <p>Uploaded by: {photoData.uploader_name}</p>
                )}
                {photoData.uploaded_at && (
                  <p>{new Date(photoData.uploaded_at).toLocaleString()}</p>
                )}
                {photoData.file_size && photoData.processed_width && photoData.processed_height && (
                  <p>Size: {formatFileSize(photoData.file_size)} • {photoData.processed_width}×{photoData.processed_height}</p>
                )}
                {slot.notes && (
                  <p className="pt-3">Note: {slot.notes}</p>
                )}
              </div>
              <ResponsiveButton
                onClick={handleDownloadPhoto}
                className="w-full flex items-center justify-center gap-2"
                variant="outline"
              >
                <Download className="w-4 h-4" />
                Download Photo
              </ResponsiveButton>
            </div>
          )}
        </div>
      </ResponsiveModal>
      
    {viewMode === 'grid' ? (
      <Card className="card-hover overflow-hidden" data-testid={`photo-slot-${slot.id}`}>
        <div 
          className="relative overflow-hidden cursor-pointer hover:opacity-90 transition-opacity bg-gray-100"
          style={{ aspectRatio: '3/4' }}
          onClick={handlePhotoClick}
        >
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            </div>
          ) : slot.has_photo && photoUrl ? (
            <img src={photoUrl} alt={slot.name} className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Camera className="w-12 h-12 text-gray-300" />
            </div>
          )}
          {/* Required/Optional badge - top left */}
          <div className="absolute top-2 left-2">
            <span
              className={`text-xs px-2 py-0.5 rounded-full shadow-lg ${
                slot.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {slot.required ? 'Required' : 'Optional'}
            </span>
          </div>
        </div>
        <CardContent className="p-4">
          <div className="mb-3">
            <h4 className="font-semibold text-gray-900 text-sm line-clamp-2">
              {slot.caption || slot.name}
            </h4>
          </div>
          <div className="space-y-2">
            {/* Hide Update button when locked */}
            {!projectLocked && (
              <Button
                size="sm"
                onClick={() => onUpload(slot, slot.has_photo ? photoData : null, photoUrl)}
                data-testid={`update-button-${slot.id}`}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                aria-label="Update Photo"
                title="Update Photo"
              >
              <Upload className="w-4 h-4 mr-1 md:hidden" />
              <Edit2 className="w-4 h-4 mr-1 hidden md:inline" />
              <span className="md:hidden">Update</span>
              <span className="hidden md:inline">Update Photo</span>
            </Button>
            )}
          </div>
        </CardContent>
      </Card>
    ) : (
      <Card className="card-hover" data-testid={`photo-slot-${slot.id}`}>
        <CardContent className="p-2">
          <div className="flex items-center gap-3">
            <div 
              className="relative overflow-hidden cursor-pointer hover:opacity-90 transition-opacity bg-gray-100 rounded-md flex-shrink-0"
              style={{ width: '80px', height: '106px' }}
              onClick={handlePhotoClick}
            >
              {loading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-blue-600 border-r-transparent"></div>
                </div>
              ) : slot.has_photo && photoUrl ? (
                <img src={photoUrl} alt={slot.name} className="w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-gray-300" />
                </div>
              )}
            </div>
            {/* Desktop layout */}
            <div className="hidden md:flex flex-1 min-w-0 flex-col">
              <h4 className="font-semibold text-gray-900 text-sm truncate mb-1">
                {slot.caption || slot.name}
              </h4>
              <span
                className={`text-xs px-2 py-0.5 rounded-full w-fit ${
                  slot.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {slot.required ? 'Required' : 'Optional'}
              </span>
            </div>
            
            {/* Mobile layout */}
            <div className="flex md:hidden flex-1 min-w-0 flex-col">
              <h4 className="font-semibold text-gray-900 text-sm truncate mb-1">
                {slot.caption || slot.name}
              </h4>
              <span
                className={`text-xs px-2 py-0.5 rounded-full w-fit ${
                  slot.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {slot.required ? 'Required' : 'Optional'}
              </span>
            </div>
            
            {/* Hide Update button when locked */}
            {!projectLocked && (
              <Button
                size="sm"
                onClick={() => onUpload(slot, slot.has_photo ? photoData : null, photoUrl)}
                data-testid={`update-button-${slot.id}`}
                className="bg-blue-500 hover:bg-blue-600 text-white flex-shrink-0"
                aria-label="Update Photo"
                title="Update Photo"
              >
                <Upload className="w-4 h-4 mr-1 md:hidden" />
                <Edit2 className="w-4 h-4 mr-1 hidden md:inline" />
                <span className="md:hidden">Update</span>
                <span className="hidden md:inline">Update Photo</span>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    )}
    </>
  );
}

export default ProjectDetail;
